import {
  type CustomFunction,
  type OutputProperty,
  type OutputPropertySupportedDataType,
} from '@blaze-shared/api-contracts-domain';

import {
  type OpenAiCustomFunctionForm,
  type OutputPropertySupportedDataTypeForm,
} from './openai-connector-functions.models';

export function parseFunctionFormValue(value?: OpenAiCustomFunctionForm): CustomFunction {
  return {
    name: value?.name || '',
    modelName: value?.modelName || '',
    systemInstruction: value?.systemInstruction || '',
    top_p: !value?.top_p && value?.top_p !== 0 ? 1 : Number(value.top_p),
    temperature: !value?.temperature && value?.temperature !== 0 ? 1 : Number(value?.temperature),
    outputSchema:
      value?.outputSchema
        ?.map((schema) => parseOutputParameterFromFormValue(schema))
        .filter((property: OutputProperty | undefined): property is OutputProperty => !!property) ||
      [],
  };
}

const MAP_FORM_OUTPUT_PROPERTY_DATA_TYPE: Record<
  OutputPropertySupportedDataTypeForm,
  OutputPropertySupportedDataType
> = {
  Bool: 'Bool',
  Int: 'Int',
  Long: 'Long',
  String: 'String',
  Date: 'Date',
  DateTime: 'DateTime',
  Time: 'Time',
  Duration: 'Duration',
  Double: 'Double',
  Decimal: 'Decimal',
  Item: 'Item',
  Enum: 'String',
  Email: 'String',
};

export function parseOutputParameterFromFormValue(
  schema?: OpenAiCustomFunctionForm['outputSchema'][number],
): OutputProperty | undefined {
  if (!schema) {
    return undefined;
  }
  const dataType: OutputPropertySupportedDataType =
    MAP_FORM_OUTPUT_PROPERTY_DATA_TYPE[schema.dataType];
  const format: string | undefined = schema.dataType === 'Email' ? 'email' : undefined;
  const result = {
    ...schema,
    dataType,
    format,
    isRequired: !schema.isOptional,
    enums:
      schema.enums?.length && schema.enums.split
        ? schema.enums.split(',').map((e) => e.trim())
        : [],
  };
  delete result.isOptional; // Remove isOptional as it's not part of the OpenAiCustomFunction model
  if (dataType !== 'Item') {
    delete result.properties;
  }
  return result;
}

export function parseFunctionToFormValue(value?: CustomFunction): OpenAiCustomFunctionForm {
  if (!value) {
    return {
      modelName: '',
      name: '',
      outputSchema: [],
      systemInstruction: '',
      top_p: 1,
      temperature: 1,
    };
  }
  return {
    ...value,
    outputSchema: value.outputSchema?.map((schema) => parseOutputParameterToFormValue(schema)),
  };
}

export function parseOutputParameterToFormValue(
  schema: OutputProperty,
): OpenAiCustomFunctionForm['outputSchema'][number] {
  let dataType: OutputPropertySupportedDataTypeForm = schema.dataType;
  if (schema.dataType === 'String' && schema.enums?.length) {
    dataType = 'Enum';
  } else if (schema.dataType === 'String' && schema.format === 'email') {
    dataType = 'Email';
  }
  const result = {
    ...schema,
    dataType,
    isOptional: !schema.isRequired,
    enums: schema.enums ? schema.enums.join(', ') : undefined,
  };
  if (result.dataType !== 'Item') {
    delete result.properties; // Remove properties if dataType is not Item (Object)
  }
  delete result.format; // Remove format as it's not part of the OpenAiCustomFunctionForm model
  delete result.isRequired; // Remove isRequired as it's not part of the OpenAiCustomFunctionForm model
  return result;
}
